// Validation for the new submission flow.
//
// The legacy `nameRegex` in helpers/validations.ts whitelists Latin-1 only
// (`/^[A-Za-zÀ-ÖØ-öø-ÿ\s-`']+$/i`), which rejects real names this funnel will
// see — Nguyễn, Łukasz, Şule, Đorđe, Иван, 李 — and it is shared by a dozen
// legacy signup surfaces, so it is not safe to change there. These new pages
// validate by EXCLUSION instead: reject digits and structural punctuation,
// allow every letter. TS 3.9 targets ES5 here, so no /u regex escapes.

import { passwordRegex } from "../../../helpers/validations";

export const NAME_MAX_LENGTH = 60;

// Characters a personal name never contains, plus digits.
const NAME_DISALLOWED = /[0-9<>@#$%^&*_=+[\]{}\\/|~`"?!;:()]/;

export const isValidName = (value: string): boolean => {
  const trimmed = (value || "").trim();
  if (!trimmed) return false;
  if (trimmed.length > NAME_MAX_LENGTH) return false;
  if (NAME_DISALLOWED.test(trimmed)) return false;
  // Must contain at least one letter — "..." or "---" is not a name. Anything
  // that is not a digit, punctuation or whitespace counts, which keeps
  // non-Latin scripts valid without needing Unicode property escapes.
  return /[^\d\s.,'-]/.test(trimmed);
};

// RFC 5321 limit. Without a cap the field accepts a 4KB "tracking alias" that
// then overflows the masked-email line on the verification screen.
export const EMAIL_MAX_LENGTH = 254;

export const isWithinEmailLength = (email: string): boolean =>
  !!email && email.trim().length <= EMAIL_MAX_LENGTH;

export const MIN_PASSWORD_LENGTH = 6;

// Delegates to the app-wide rule in helpers/validations.ts — the same one the
// traditional actor signup (ActorCredentialStep) applies — so the form, the
// mock "server" and every other signup surface agree on what is valid.
//
// Note that shared regex is a character WHITELIST, so it also refuses
// ( ) [ ] { } | ~ ' " and accented letters even when the 6-chars/letter/digit
// rule is met. That is deliberate parity with the existing flow.
export const isValidPassword = (value: string): boolean =>
  passwordRegex.test(value || "");
